import { useEffect, useMemo, useRef, useState } from 'react'
import { Link, Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom'

import type { Benutzer, BereichId } from './typen'
import { api, beiKeinZugang, beiLadung, beiSitzungsende } from './api'
import { notierungenApi } from './api_notierungen'
import { BEREICHE, bereichFuerPfad, bereichVon, erlaubteBereiche, liegtUnter, type Bereich, type ReiterGruppe, type Zaehler } from './bereiche'
import { useLaden } from './hooks'
import Reiter from './komponenten/Reiter'
import { assistentPfad } from './pfade'
import { SitzungKontext } from './sitzung'

import AnmeldungSeite from './seiten/AnmeldungSeite'
import AssistentSeite from './seiten/AssistentSeite'
import BereichAuswahlSeite from './seiten/BereichAuswahlSeite'
import BestellenSeite from './seiten/BestellenSeite'
import DispositionSeite from './seiten/DispositionSeite'
import EinstellungenSeite from './seiten/EinstellungenSeite'
import ErfassenSeite from './seiten/ErfassenSeite'
import ExcelImportSeite from './seiten/ExcelImportSeite'
import FaelligSeite from './seiten/FaelligSeite'
import ForecastSeite from './seiten/ForecastSeite'
import ImporteSeite from './seiten/ImporteSeite'
import InventurSeite from './seiten/InventurSeite'
import KeinZugangSeite from './seiten/KeinZugangSeite'
import KladdeSeite from './seiten/KladdeSeite'
import LieferantenSeite from './seiten/LieferantenSeite'
import RegelnSeite from './seiten/RegelnSeite'
import RueckfragenSeite from './seiten/RueckfragenSeite'
import StammdatenSeite from './seiten/StammdatenSeite'
import NotierungenEingaengeSeite from './seiten/notierungen/EingaengeSeite'
import NotierungenErfassenSeite from './seiten/notierungen/ErfassenSeite'
import NotierungenHeuteSeite from './seiten/notierungen/HeuteSeite'
import NotierungenImporteSeite from './seiten/notierungen/ImporteSeite'
import NotierungenLaufSeite from './seiten/notierungen/LaufSeite'
import NotierungenStammdatenSeite from './seiten/notierungen/StammdatenSeite'
import NotierungenTabelleSeite from './seiten/notierungen/TabelleSeite'
import NotierungenVerlaufSeite from './seiten/notierungen/VerlaufSeite'

/**
 * Das Gerüst: Kopf mit Bereichsumschalter, Menü und Reiter des aktiven Bereichs, Frage an dessen
 * Assistenten, Benutzer. Welcher Bereich was zeigt, steht in bereiche.ts; hier stehen nur die Routen.
 */

/** Eine Zeile im Kopf: was man auf jeder Seite dem Assistenten des Bereichs sagen kann, ohne erst
 *  hinzugehen. „Bestell 5 Paletten Sanna Boden" oder „Ist heute Einkaufstag für Palmöl?" - Enter, und
 *  das Gespräch beginnt mit dieser Frage. */
function AssistentFrage({ bereich }: { bereich: BereichId }) {
  const navigiere = useNavigate()
  const [text, setText] = useState('')
  function absenden() {
    const frage = text.trim()
    if (!frage) return
    setText('')
    navigiere(assistentPfad(frage, bereich))
  }
  return (
    <input
      className="frage"
      type="text"
      placeholder="Dem Assistenten sagen…"
      title="Frage oder Auftrag in Worten - Enter startet ein Gespräch mit dem Assistenten dieses Bereichs."
      value={text}
      onChange={(e) => setText(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === 'Enter') absenden()
      }}
    />
  )
}

/** Der Bereichsumschalter: ein Titel, der zugleich die Auswahl ist - nur mit mehr als einem Bereich. */
function Bereichswahl({ bereich, erlaubt }: { bereich: Bereich | null; erlaubt: BereichId[] }) {
  const navigiere = useNavigate()
  if (erlaubt.length < 2) return <h1>{bereich && erlaubt.includes(bereich.id) ? bereich.titel : bereichVon(erlaubt[0] ?? 'verpackung').titel}</h1>
  return (
    <select
      className="bereichswahl"
      aria-label="Bereich wechseln"
      title="Bereich wechseln"
      value={bereich?.id ?? ''}
      onChange={(e) => navigiere(e.target.value ? bereichVon(e.target.value as BereichId).startpfad : '/')}
    >
      {bereich === null && <option value="">Othüna - Bereich wählen</option>}
      {BEREICHE.filter((b) => erlaubt.includes(b.id)).map((b) => (
        <option key={b.id} value={b.id}>
          {b.titel}
        </option>
      ))}
    </select>
  )
}

/** Die Zahlen an Menüpunkten und Reitern, nur für den aktiven Bereich - die anderen fragt niemand ab. */
function useZaehler(bereich: BereichId | null): Record<Zaehler, number> {
  const [stand, setStand] = useState<Record<Zaehler, number>>({ rueckfragen: 0, notierungen_pruefen: 0 })
  useEffect(() => {
    if (bereich === null) return
    const laden = () => {
      if (bereich === 'verpackung') {
        api
          .rueckfragen()
          .then((werte) => setStand((alt) => ({ ...alt, rueckfragen: werte.length })))
          .catch(() => setStand((alt) => ({ ...alt, rueckfragen: 0 })))
      } else {
        notierungenApi
          .laeufe('pruefen')
          .then((werte) => setStand((alt) => ({ ...alt, notierungen_pruefen: werte.length })))
          .catch(() => setStand((alt) => ({ ...alt, notierungen_pruefen: 0 })))
      }
    }
    laden()
    const takt = window.setInterval(laden, 60_000)
    return () => window.clearInterval(takt)
  }, [bereich])
  return stand
}

/** Die Reiterzeile einer Gruppe; Reiter, die das Backend liefert (Gruppen der Tabelle), werden geladen. */
function Reiterzeile({ gruppe, zaehler }: { gruppe: ReiterGruppe; zaehler: Record<Zaehler, number> }) {
  const { daten } = useLaden(() => (gruppe.laden ? gruppe.laden() : Promise.resolve(gruppe.eintraege ?? [])), [gruppe])
  const eintraege = gruppe.eintraege ?? daten ?? []
  if (eintraege.length === 0) return null
  return <Reiter eintraege={eintraege.map((e) => ({ ...e, zaehler: e.zaehler ? zaehler[e.zaehler] : undefined }))} />
}

export default function App() {
  const { pathname } = useLocation()
  // Wer angemeldet ist: `undefined` heißt „wird noch geprüft", `null` heißt „niemand".
  const [benutzer, setBenutzer] = useState<Benutzer | null | undefined>(undefined)
  useEffect(() => {
    api
      .ich()
      .then(setBenutzer)
      .catch(() => setBenutzer(null))
  }, [])
  // Läuft die Sitzung ab, während jemand arbeitet: zurück zum Anmeldeschirm statt roter Meldungen.
  useEffect(() => beiSitzungsende(() => setBenutzer(null)), [])
  // Sperrt das Backend einen Bereich (403), steht statt der Seite „Kein Zugang" - bis zum nächsten Pfad.
  const [gesperrtText, setGesperrtText] = useState<string | null>(null)
  useEffect(() => beiKeinZugang(setGesperrtText), [])
  useEffect(() => setGesperrtText(null), [pathname])
  // Läuft eine Anfrage, zeigt ein dünner Balken unter dem Kopf das an - statt einer leeren Seite.
  const [laedt, setLaedt] = useState(false)
  useEffect(() => beiLadung((anzahl) => setLaedt(anzahl > 0)), [])
  // Die Kopfzeile bricht bei schmalem Fenster um und wird höher - Seiten, die die volle Höhe nutzen
  // (der Chat), rechnen mit der gemessenen Höhe statt mit einer Konstante.
  const kopf = useRef<HTMLElement>(null)
  useEffect(() => {
    const el = kopf.current
    if (!el || typeof ResizeObserver === 'undefined') return
    const setzen = () => document.documentElement.style.setProperty('--kopfhoehe', `${el.offsetHeight + 4}px`)
    setzen()
    const beobachter = new ResizeObserver(setzen)
    beobachter.observe(el)
    return () => beobachter.disconnect()
  }, [benutzer])

  const bereich = bereichFuerPfad(pathname)
  const erlaubt = useMemo(() => (benutzer ? erlaubteBereiche(benutzer) : []), [benutzer])
  const zugang = bereich === null || erlaubt.includes(bereich.id)
  const zaehler = useZaehler(benutzer && bereich && zugang ? bereich.id : null)
  useEffect(() => {
    document.title = bereich ? `Othüna ${bereich.titel}` : 'Othüna'
  }, [bereich])

  async function abmelden() {
    try {
      await api.abmelden()
    } finally {
      // Auch wenn der Server nicht antwortet: die Oberfläche geht zu, das Cookie ist dann spätestens
      // beim nächsten Aufruf ungültig.
      setBenutzer(null)
    }
  }

  if (benutzer === undefined) {
    return <div className="anmeldung"><p className="hinweis">Anmeldung wird geprüft…</p></div>
  }
  if (benutzer === null) {
    return <AnmeldungSeite onAngemeldet={setBenutzer} />
  }

  const offen = bereich !== null && zugang && gesperrtText === null
  const reiter = offen ? bereich.reiter.find((r) => liegtUnter(pathname, r.aktivAuf)) : undefined

  return (
    <SitzungKontext.Provider value={{ benutzer, bereiche: erlaubt }}>
      <header className="kopf" ref={kopf}>
        <Bereichswahl bereich={bereich} erlaubt={erlaubt} />
        {offen && (
          <nav>
            {/* Kein NavLink: aktiv ist ein Menüpunkt auch auf seinen Unterseiten (Kladde gehört zur Disposition). */}
            {bereich.menue.map((punkt) => {
              const aktiv = liegtUnter(pathname, punkt.aktivAuf)
              const klassen = [aktiv ? 'aktiv' : '', punkt.abgesetzt ? 'abgesetzt' : ''].filter(Boolean).join(' ')
              const zahl = punkt.zaehler ? zaehler[punkt.zaehler] : 0
              return (
                <Link key={punkt.pfad} to={punkt.pfad} className={klassen || undefined} aria-current={aktiv ? 'page' : undefined}>
                  {punkt.titel}
                  {zahl > 0 && (
                    <span className="zaehler" title={punkt.zaehler === 'rueckfragen' ? `${zahl} Rückfrage(n) warten` : `${zahl} Lauf/Läufe zu prüfen`}>
                      {zahl}
                    </span>
                  )}
                </Link>
              )
            })}
          </nav>
        )}
        {/* Auf der Assistenten-Seite gibt es das Chatfeld - ein zweites Feld im Kopf wäre doppelt. */}
        {offen && !liegtUnter(pathname, [`${bereich.praefix}/assistent`]) && <AssistentFrage bereich={bereich.id} />}
        <div className="kopf-benutzer">
          <span title={`Angemeldet als ${benutzer.name}`}>{benutzer.anzeigename}</span>
          <button className="leise" onClick={() => void abmelden()}>
            Abmelden
          </button>
        </div>
      </header>
      <div className={`ladebalken${laedt ? ' aktiv' : ''}`} aria-hidden="true" />
      {reiter && <Reiterzeile gruppe={reiter} zaehler={zaehler} />}

      <main>
        {bereich !== null && !zugang ? (
          <KeinZugangSeite bereich={bereich} />
        ) : gesperrtText !== null && bereich !== null ? (
          <KeinZugangSeite bereich={bereich} text={gesperrtText} />
        ) : (
          <Routes>
            <Route path="/" element={<BereichAuswahlSeite />} />

            {/* Verpackungsdisposition - alle Adressen von vor dem 06.10.2026 bleiben gültig. */}
            <Route path="/assistent" element={<AssistentSeite key="verpackung" bereich="verpackung" />} />
            <Route path="/disposition" element={<DispositionSeite />} />
            <Route path="/bestellen" element={<BestellenSeite />} />
            <Route path="/heute" element={<FaelligSeite />} />
            <Route path="/faellig" element={<FaelligSeite />} />
            <Route path="/forecast" element={<ForecastSeite />} />
            <Route path="/lieferanten" element={<LieferantenSeite />} />
            <Route path="/kladde" element={<KladdeSeite />} />
            <Route path="/inventur" element={<InventurSeite />} />
            <Route path="/importe" element={<ImporteSeite />} />
            <Route path="/importe/excel/:id" element={<ExcelImportSeite />} />
            <Route path="/importe/:id" element={<ImporteSeite />} />
            <Route path="/erfassen" element={<ErfassenSeite />} />
            <Route path="/rueckfragen" element={<RueckfragenSeite />} />
            <Route path="/regeln" element={<RegelnSeite />} />
            <Route path="/stammdaten" element={<StammdatenSeite />} />
            <Route path="/einstellungen" element={<EinstellungenSeite />} />

            {/* Rohstoffnotierungen */}
            <Route path="/notierungen" element={<Navigate to={bereichVon('notierungen').startpfad} replace />} />
            <Route path="/notierungen/assistent" element={<AssistentSeite key="notierungen" bereich="notierungen" />} />
            <Route path="/notierungen/heute" element={<NotierungenHeuteSeite />} />
            <Route path="/notierungen/tabelle" element={<NotierungenTabelleSeite />} />
            <Route path="/notierungen/tabelle/:gruppe" element={<NotierungenTabelleSeite />} />
            <Route path="/notierungen/verlauf" element={<NotierungenVerlaufSeite />} />
            <Route path="/notierungen/importe" element={<NotierungenImporteSeite />} />
            <Route path="/notierungen/importe/:id" element={<NotierungenLaufSeite />} />
            <Route path="/notierungen/erfassen" element={<NotierungenErfassenSeite />} />
            <Route path="/notierungen/eingaenge" element={<NotierungenEingaengeSeite />} />
            <Route path="/notierungen/stammdaten" element={<NotierungenStammdatenSeite />} />
            <Route path="/notierungen/stammdaten/:teil" element={<NotierungenStammdatenSeite />} />
            <Route path="/notierungen/*" element={<Navigate to={bereichVon('notierungen').startpfad} replace />} />

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        )}
      </main>
    </SitzungKontext.Provider>
  )
}
